import { localDateFromInstant } from './timeNormalization.ts'
import { weekdayIdFromLocalDate } from './resolvePhysiologicalDay.ts'
import { normalizeTimeHHMM, preferredWorkoutStart, workoutEndTimeFromStart } from './trainingSchedule.ts'

/** Default local times (minutes from midnight) for meal blocks when time_stamp is missing. */
export const MEAL_BLOCK_DEFAULT_MINUTES: Record<string, number> = {
  Breakfast: 7 * 60 + 30,
  'Morning Snack': 10 * 60,
  Lunch: 12 * 60 + 30,
  Snack: 15 * 60,
  'Afternoon Snack': 16 * 60,
  Dinner: 19 * 60,
}

export const AFTERNOON_START_MINUTES = 12 * 60
export const EVENING_START_MINUTES = 18 * 60
export const MEAL_GRACE_MINUTES = 20
export const DEFAULT_WORKOUT_DURATION_MINUTES = 60

export type RegistrationDayPhase = 'morning' | 'afternoon' | 'evening'

export type ProtocolDayAnchor = {
  timezone: string
  localDate: string
  todayDayId: string
  localMinutes: number
  phase: RegistrationDayPhase
  /** When true, no meals or training should be offered for todayDayId. */
  deferTodayToTomorrow: boolean
}

export function parseTimeToMinutes(timeStamp: string): number {
  const normalized = normalizeTimeHHMM(String(timeStamp ?? ''))
  const [h, m] = normalized.split(':').map(Number)
  if (!Number.isFinite(h) || !Number.isFinite(m)) return MEAL_BLOCK_DEFAULT_MINUTES.Lunch
  return h * 60 + m
}

export function mealBlockMinutes(blockName: string, timeStamp?: string | null): number {
  if (timeStamp) return parseTimeToMinutes(timeStamp)
  return MEAL_BLOCK_DEFAULT_MINUTES[blockName] ?? MEAL_BLOCK_DEFAULT_MINUTES.Lunch
}

export function localMinutesFromInstant(iso: string, timezone: string): number {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone || 'UTC',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  }).formatToParts(new Date(iso))

  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? 0)
  const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? 0)
  return hour * 60 + minute
}

export function resolveRegistrationDayPhase(localMinutes: number): RegistrationDayPhase {
  if (localMinutes >= EVENING_START_MINUTES) return 'evening'
  if (localMinutes >= AFTERNOON_START_MINUTES) return 'afternoon'
  return 'morning'
}

export function resolveProtocolDayAnchor(
  profile: Record<string, unknown>,
  nowIso = new Date().toISOString(),
): ProtocolDayAnchor {
  const timezone = String(profile.timezone ?? 'UTC')
  const localDate = localDateFromInstant(nowIso, timezone)
  const todayDayId = weekdayIdFromLocalDate(localDate, timezone)
  const localMinutes = localMinutesFromInstant(nowIso, timezone)
  const phase = resolveRegistrationDayPhase(localMinutes)

  return {
    timezone,
    localDate,
    todayDayId,
    localMinutes,
    phase,
    deferTodayToTomorrow: phase === 'evening',
  }
}

export function shouldIncludeMealForRegistrationDay(
  meal: { day_id?: unknown; block_name?: unknown; time_stamp?: unknown },
  anchor: ProtocolDayAnchor,
): boolean {
  const dayId = String(meal.day_id ?? '').toLowerCase()
  if (dayId !== anchor.todayDayId) return true

  if (anchor.deferTodayToTomorrow) return false

  const blockName = String(meal.block_name ?? '')
  const blockMinutes = mealBlockMinutes(blockName, meal.time_stamp != null ? String(meal.time_stamp) : null)

  if (anchor.phase === 'afternoon') {
    return blockName === 'Dinner'
  }

  return blockMinutes + MEAL_GRACE_MINUTES >= anchor.localMinutes
}

export function filterMealsForRegistrationAnchor(
  meals: Record<string, unknown>[],
  profile: Record<string, unknown>,
  nowIso = new Date().toISOString(),
): Record<string, unknown>[] {
  const anchor = resolveProtocolDayAnchor(profile, nowIso)
  return meals.filter((meal) => shouldIncludeMealForRegistrationDay(meal, anchor))
}

export function shouldSkipTrainingForRegistrationDay(
  block: { day_name_id?: unknown; exercises?: unknown[]; window_type?: unknown },
  profile: Record<string, unknown>,
  nowIso = new Date().toISOString(),
): boolean {
  const anchor = resolveProtocolDayAnchor(profile, nowIso)
  const dayId = String(block.day_name_id ?? '').toLowerCase()
  if (dayId !== anchor.todayDayId) return false

  if (anchor.deferTodayToTomorrow) return true

  const exercises = (block.exercises as unknown[] | undefined) ?? []
  const windowType = String(block.window_type ?? '').toLowerCase()
  if (exercises.length === 0 || windowType === 'recovery' || windowType === 'rest') {
    return false
  }

  const preferredStart = preferredWorkoutStart(profile) ?? '07:00'
  const workoutEnd = workoutEndTimeFromStart(preferredStart, DEFAULT_WORKOUT_DURATION_MINUTES)
  const endMinutes = parseTimeToMinutes(workoutEnd)

  return anchor.localMinutes >= endMinutes
}

export function filterTrainingScheduleForRegistrationAnchor(
  schedule: Record<string, unknown>[],
  profile: Record<string, unknown>,
  nowIso = new Date().toISOString(),
): Record<string, unknown>[] {
  return schedule.filter((block) => !shouldSkipTrainingForRegistrationDay(block, profile, nowIso))
}

/** Client/runtime filter — same rules as registration persist. */
export function filterMealsForCurrentLocalDay<T extends { day_id: string; block_name: string; time_stamp?: string }>(
  meals: T[],
  profile: { timezone?: string | null },
  now = new Date(),
): T[] {
  const anchor = resolveProtocolDayAnchor({ timezone: profile.timezone ?? 'UTC' }, now.toISOString())
  return meals.filter((meal) => shouldIncludeMealForRegistrationDay(meal, anchor))
}

export function defaultMealsSelectedDate(
  profile: { timezone?: string | null },
  meals: { day_id: string; block_name: string; time_stamp?: string }[],
  todayIso: string,
  todayDayId: string,
  now = new Date(),
): string {
  const visibleToday = filterMealsForCurrentLocalDay(
    meals.filter((m) => m.day_id === todayDayId),
    profile,
    now,
  )
  if (visibleToday.length > 0) return todayIso

  const anchor = resolveProtocolDayAnchor({ timezone: profile.timezone ?? 'UTC' }, now.toISOString())
  if (!anchor.deferTodayToTomorrow) return todayIso

  const tomorrow = new Date(todayIso + 'T12:00:00')
  tomorrow.setDate(tomorrow.getDate() + 1)
  return tomorrow.toISOString().slice(0, 10)
}
